import { useCallback, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { faArrowRotateRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Sentry from 'src/services/sentry';

import CustomTable from 'src/components/custom_components/CustomTable/CustomTable';
import WarnNoConnectedCameraModal from 'src/components/custom_components/WarnNoConnectedCameraModal';
import { ResumeInformationModal } from 'src/components/ResumeInformationModal';

import { Api } from 'src/models/Api.model';
import { CriteriaGroupGroupElementsInnerOperatorEnum } from 'src/models/GalenData/CriteriaGroupGroupElementsInner.model';
import { RetinalExamKeyFiledEnum } from 'src/models/GalenData/CustomDeviceDataModel.model';
import { DeviceDataView } from 'src/models/GalenData/DeviceDataView.model';
import { OrderData } from 'src/models/GalenData/Order.model';
import { PageOfDeviceDataView } from 'src/models/GalenData/PageOfDeviceDataView.model';
import { I18NextNS } from 'src/models/Language.model';
import { Session, transformExamToSession } from 'src/models/Session.model';

import useCheckCameraConnection from 'src/hooks/useCheckCameraConnection';
import useGetRetinalExamDataModelConfig from 'src/hooks/useGetRetinalExamDataModelConfig';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

const DEFAULT_PAGE_SIZE = 10;

export type OpenOrdersTableProps = {
  ordersData?: PageOfDeviceDataView<OrderData>;
  isLoading: boolean;
  onFetchOrders: (pageNumber: number, pageSize: number) => Promise<void>;
  onPageChange: (page: number) => void;
  currentPage?: number;
};

export default function OpenOrdersTable({
  ordersData,
  isLoading,
  onFetchOrders,
  onPageChange,
  currentPage = 1,
}: OpenOrdersTableProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const { deviceDataModelId } = useGetRetinalExamDataModelConfig();
  const { handleHttpRequestError } = useHandleHttpRequestError();
  const { isCameraConnected } = useCheckCameraConnection();

  const [currentSession, setCurrentSession] = useState<Session>();
  const [isShowResumeModal, setIsShowResumeModal] = useState(false);
  const [
    shouldShowWarnNoConnectedCameraModal,
    setShouldShowWarnNoConnectedCameraModal,
  ] = useState(false);

  const handleResumeOrder = useCallback(
    async (data: DeviceDataView<OrderData>) => {
      try {
        const orderId = data.data.OrderId.value;
        const examResponse = await Api.remote.get_exams({
          deviceDataModelId,
          ownerFilter: {
            users: [data.ownerId ?? ''],
          },
          groupElements: [
            {
              key: RetinalExamKeyFiledEnum.IsHidden,
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
              value: false,
            },
            {
              key: RetinalExamKeyFiledEnum.OrderId,
              value: orderId,
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            },
          ],
        });
        const exam = examResponse.data.content?.[0];
        if (!exam) {
          Sentry.captureMessage(`No exam found for order ID: ${orderId}`);
          return;
        }
        setCurrentSession(transformExamToSession(exam));
        setIsShowResumeModal(true);
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: t('bootbox.title.Error'),
          message: errorMessage,
          locale: language,
          type: 'error',
        });
      }
    },
    [
      deviceDataModelId,
      setCurrentSession,
      setIsShowResumeModal,
      t,
      language,
      handleHttpRequestError,
    ],
  );

  return (
    <>
      <CustomTable
        isLoading={isLoading}
        dataSource={ordersData?.content ?? []}
        rowKey="deviceDataId"
        pagination={{
          defaultPageSize: DEFAULT_PAGE_SIZE,
          pageSizeOptions: [DEFAULT_PAGE_SIZE],
          totalPages: ordersData?.totalPages ?? 0,
          currentPage: currentPage,
          handleSetCurrentPage: (page) => {
            onPageChange(page);
          },
          onChangePage: (page, pageSize) => {
            onFetchOrders(page - 1, pageSize);
          },
        }}
        columns={[
          {
            title: t('patientFields.DOB'),
            dataIndex: t('patientFields.DOB'),
            render: (data: DeviceDataView<OrderData>) =>
              data.owner?.dateOfBirth,
          },
          {
            title: t('patientFields.First Name'),
            dataIndex: t('patientFields.First Name'),
            render: (data: DeviceDataView<OrderData>) => data.owner?.firstName,
          },
          {
            title: t('patientFields.Last Name'),
            dataIndex: t('patientFields.Last Name'),
            render: (data: DeviceDataView<OrderData>) => data.owner?.lastName,
          },
          {
            title: t('patientFields.PID'),
            dataIndex: t('patientFields.PID'),
            render: (data: DeviceDataView<OrderData>) => data.owner?.patientId,
          },
          {
            title: 'Sites',
            dataIndex: 'Sites',
            render: (data: DeviceDataView<OrderData>) =>
              data.data?.SiteName?.value,
          },
          {
            title: t('orderFields.actions'),
            dataIndex: t('orderFields.actions'),
            render: (data: DeviceDataView<OrderData>) => {
              return (
                <Button
                  variant="outline-primary"
                  className="rounded-3 px-3 py-1"
                  onClick={() => {
                    if (!isCameraConnected) {
                      setShouldShowWarnNoConnectedCameraModal(true);
                      return;
                    }
                    handleResumeOrder(data);
                  }}
                >
                  <FontAwesomeIcon icon={faArrowRotateRight} />
                </Button>
              );
            },
          },
        ]}
        empty={{ buttons: [], text: t('message.error.noOrdersToDisplay') }}
      />

      {currentSession && (
        <ResumeInformationModal
          currentSession={currentSession}
          isShowResumeModal={isShowResumeModal}
          handleSetIsShowResumeModal={setIsShowResumeModal}
        />
      )}
      <WarnNoConnectedCameraModal
        isVisible={shouldShowWarnNoConnectedCameraModal}
      />
    </>
  );
}
